import React from 'react';
import { Box, Text } from 'ink';
import type { HelpDialogInputState } from './types';
import { Link } from './lib/components/Link';
import { useKeyHandler } from './lib/key_handler';
import { isGlab } from './lib/environment_context';
import { useTheme } from './lib/theme';

export interface HelpDialogCallbacks {
  onCloseHelp: () => void;
}

interface HelpDialogProps {
  input: HelpDialogInputState;
  callbacks: HelpDialogCallbacks;
}

interface ShortcutItem {
  action: string;
  description: string;
}

const shortcuts: ShortcutItem[] = [
  { action: '@', description: 'Search and attach files to your prompt' },
  { action: '/', description: 'Execute slash commands' },
  { action: 'Ctrl + R', description: 'Search prompt history' },
  { action: 'Ctrl + C', description: 'Exit GitLab Duo CLI' },
  { action: 'Up/Down', description: 'Navigate input history' },
];

export const helpFooterHint = (): string | null => 'Esc to close';

export const HelpDialog: React.FC<HelpDialogProps> = ({ input, callbacks }) => {
  const { tokens } = useTheme();
  useKeyHandler(async (event) => {
    if (event.name === 'escape') {
      callbacks.onCloseHelp();
      event.stopPropagation();
    }
  });

  return (
    <Box flexDirection="column">
      <Box
        borderStyle="round"
        borderColor={tokens.text.accent}
        paddingX={2}
        paddingY={1}
        flexDirection="column"
      >
        <Box marginBottom={1}>
          <Text bold color={tokens.text.accent}>
            GitLab Duo CLI{isGlab() ? ' (glab)' : ''}
          </Text>
        </Box>

        <Box marginBottom={1}>
          <Text>
            An AI-powered terminal assistant that understands your codebase, helps with development
            tasks, and executes commands with your permission.
          </Text>
        </Box>

        <Box marginBottom={1}>
          <Text bold color={tokens.text.warning}>
            Shortcuts
          </Text>
        </Box>

        <Box flexDirection="column" marginBottom={1}>
          {shortcuts.map((shortcut) => (
            <Box key={shortcut.action}>
              <Box width={16}>
                <Text bold color={tokens.text.success}>
                  {shortcut.action}
                </Text>
              </Box>
              <Text>{shortcut.description}</Text>
            </Box>
          ))}
        </Box>

        <Box marginBottom={1}>
          <Text bold color={tokens.text.warning}>
            Slash Commands
          </Text>
        </Box>

        <Box flexDirection="column" marginBottom={1}>
          {input.slashCommands && input.slashCommands.length > 0 ? (
            input.slashCommands.map((command) => (
              <Box key={command.name}>
                <Box width={16}>
                  <Text bold color={tokens.text.success}>
                    {command.name}
                  </Text>
                </Box>
                <Text>{command.description}</Text>
              </Box>
            ))
          ) : (
            <>
              <Box>
                <Box width={16}>
                  <Text bold color={tokens.text.success}>
                    /help
                  </Text>
                </Box>
                <Text>Show this help dialog</Text>
              </Box>
              <Box>
                <Box width={16}>
                  <Text bold color={tokens.text.success}>
                    /new
                  </Text>
                </Box>
                <Text>Start a new chat session</Text>
              </Box>
            </>
          )}
        </Box>

        <Box>
          <Text color={tokens.text.subtle}>For more information: </Text>
          <Link url="https://docs.gitlab.com/user/gitlab_duo_cli/" color={tokens.text.subtle}>
            https://docs.gitlab.com/user/gitlab_duo_cli/
          </Link>
        </Box>
      </Box>
    </Box>
  );
};
